if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Thumbnail scroll arrow controller: vertical on desktop (>=750px), horizontal on mobile
        const prevButton = this.elements.thumbnails.querySelector('.slider-button--prev');
        const nextButton = this.elements.thumbnails.querySelector('.slider-button--next');
        const list = this.elements.thumbnails.querySelector('.thumbnail-list') || this.elements.thumbnails.slider;
        
        if (prevButton && nextButton && list) {
          prevButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.innerWidth >= 750) {
              list.scrollBy({ top: -140, behavior: 'smooth' });
            } else {
              list.scrollBy({ left: -228, behavior: 'smooth' });
            }
          });
          nextButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.innerWidth >= 750) {
              list.scrollBy({ top: 140, behavior: 'smooth' });
            } else {
              list.scrollBy({ left: 228, behavior: 'smooth' });
            }
          });

          // Dynamic arrow scroll indicator states
          const updateScrollButtons = () => {
            if (window.innerWidth >= 750) {
              const isScrollable = list.scrollHeight > list.clientHeight + 4;
              if (!isScrollable) {
                prevButton.style.display = 'none';
                nextButton.style.display = 'none';
                return;
              } else {
                prevButton.style.display = 'flex';
                nextButton.style.display = 'flex';
              }

              const scrollTop = list.scrollTop;
              const maxScrollTop = list.scrollHeight - list.clientHeight;

              // UP button state
              if (scrollTop <= 4) {
                prevButton.setAttribute('disabled', 'true');
                prevButton.style.opacity = '0.2';
                prevButton.style.pointerEvents = 'none';
              } else {
                prevButton.removeAttribute('disabled');
                prevButton.style.opacity = '1';
                prevButton.style.pointerEvents = 'auto';
              }

              // DOWN button state
              if (scrollTop >= maxScrollTop - 4) {
                nextButton.setAttribute('disabled', 'true');
                nextButton.style.opacity = '0.2';
                nextButton.style.pointerEvents = 'none';
              } else {
                nextButton.removeAttribute('disabled');
                nextButton.style.opacity = '1';
                nextButton.style.pointerEvents = 'auto';
              }
            } else {
              // Mobile horizontal handling
              const isScrollable = list.scrollWidth > list.clientWidth + 5;
              if (!isScrollable) {
                prevButton.style.display = 'none';
                nextButton.style.display = 'none';
                return;
              } else {
                prevButton.style.display = 'flex';
                nextButton.style.display = 'flex';
              }

              const scrollLeft = list.scrollLeft;
              const maxScrollLeft = list.scrollWidth - list.clientWidth;

              if (scrollLeft <= 4) {
                prevButton.setAttribute('disabled', 'true');
                prevButton.style.opacity = '0.25';
                prevButton.style.pointerEvents = 'none';
              } else {
                prevButton.removeAttribute('disabled');
                prevButton.style.opacity = '1';
                prevButton.style.pointerEvents = 'auto';
              }

              if (scrollLeft >= maxScrollLeft - 4) {
                nextButton.setAttribute('disabled', 'true');
                nextButton.style.opacity = '0.25';
                nextButton.style.pointerEvents = 'none';
              } else {
                nextButton.removeAttribute('disabled');
                nextButton.style.opacity = '1';
                nextButton.style.pointerEvents = 'auto';
              }
            }
          };

          list.addEventListener('scroll', updateScrollButtons);
          window.addEventListener('resize', updateScrollButtons);
          setTimeout(updateScrollButtons, 300);
          this.updateScrollButtons = updateScrollButtons;
        }
      }

      onSlideChanged(event) {
        const currentElement = event.detail?.currentElement;
        if (!currentElement || !this.elements.thumbnails) return;
        const mediaId = currentElement.dataset?.mediaId || currentElement.closest('[data-media-id]')?.dataset?.mediaId;
        if (!mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            const slider = this.elements.viewer.slider || activeMedia.parentElement;
            if (activeMedia.scrollIntoView) {
              activeMedia.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
            } else if (slider) {
              slider.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
            }
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        // Center active thumbnail: vertical on desktop (>=750px), horizontal on mobile
        const list = this.elements.thumbnails.querySelector('.thumbnail-list') || this.elements.thumbnails.slider;
        if (list) {
          const listRect = list.getBoundingClientRect();
          const thumbRect = thumbnail.getBoundingClientRect();
          
          if (window.innerWidth >= 750) {
            const scrollTopTarget = list.scrollTop + (thumbRect.top - listRect.top) - (listRect.height / 2) + (thumbRect.height / 2);
            list.scrollTo({ top: scrollTopTarget, behavior: 'smooth' });
          } else {
            const scrollLeftTarget = list.scrollLeft + (thumbRect.left - listRect.left) - (listRect.width / 2) + (thumbRect.width / 2);
            list.scrollTo({ left: scrollLeftTarget, behavior: 'smooth' });
          }
          
          if (this.updateScrollButtons) {
            setTimeout(this.updateScrollButtons, 100);
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
